import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, RotateCcw, Settings } from 'lucide-react';
import { Button } from '@/financeBotV2/src/components/ui/button';
import { Separator } from '@/financeBotV2/src/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/financeBotV2/src/components/ui/tooltip';
import ThemePresetSelect from '@/financeBotV2/src/components/ThemePresetSelect';
import { ChartColorSelector } from '@/financeBotV2/src/components/dashboard/ChartColorSelector';
import { useEditorStore } from '@/financeBotV2/src/store/editor-store';
import { useThemePresetStore } from '@/financeBotV2/src/store/theme-preset-store';
import {
  CHART_PALETTE_OPTIONS,
  DEFAULT_CUSTOM_CHART_COLORS,
  type ChartPaletteId,
} from '@/financeBotV2/src/utils/chart-palettes';
import { getPresetThemeStyles } from '@/financeBotV2/src/utils/theme-preset-helper';

const DEFAULT_PRESET = 'default';
const RESET_THEME_PRESET = 'smart-insights';
const DEFAULT_CHART_PALETTE: ChartPaletteId = 'brand';
const FALLBACK_STYLE = '#ffffff';

const getThemeChartColors = (styles: Record<string, string>): string[] => [
  styles['chart-1'] || styles.primary || '#6E3450',
  styles['chart-2'] || styles.secondary || '#67ADCA',
  styles['chart-3'] || styles.accent || '#C9A24E',
  styles['chart-4'] || styles.primary || '#3F7D4F',
  styles['chart-5'] || styles.secondary || '#B23A48',
];

const resolveChartColors = (
  paletteId: ChartPaletteId,
  customColors: string[],
  themeColors: string[]
): string[] => {
  if (paletteId === 'custom') return customColors.length ? customColors : DEFAULT_CUSTOM_CHART_COLORS;
  if (paletteId === 'theme') return themeColors;
  return CHART_PALETTE_OPTIONS.find((option) => option.id === paletteId)?.colors ?? themeColors;
};

const mixWithWhite = (color: string, amount = 12): string => `color-mix(in srgb, ${color} ${amount}%, #ffffff)`;

const areStringArraysEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const IconResetButton: React.FC<{
  label: string;
  disabled: boolean;
  onClick: () => void;
}> = ({ label, disabled, onClick }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        className="h-10 w-10 shrink-0 border border-transparent text-muted-foreground hover:border-border hover:bg-muted hover:text-foreground"
      >
        <RotateCcw className="h-4 w-4" />
      </Button>
    </TooltipTrigger>
    <TooltipContent side="top">{label}</TooltipContent>
  </Tooltip>
);

const AppearancePreview: React.FC<{
  styles: Record<string, string>;
  chartColors: string[];
}> = ({ styles, chartColors }) => {
  const background = styles.background || FALLBACK_STYLE;
  const foreground = styles.foreground || '#111827';
  const card = styles.card || FALLBACK_STYLE;
  const cardForeground = styles['card-foreground'] || foreground;
  const shellBackground = styles.secondary || background;
  const primary = styles.primary || '#8d3165';
  const primaryForeground = styles['primary-foreground'] || '#ffffff';
  const mutedForeground = styles['muted-foreground'] || '#6b7280';
  const neutralBorder = '#e2e8f0';
  const neutralSurface = '#ffffff';
  const neutralMuted = '#f8fafc';
  const activeTint = mixWithWhite(primary, 10);

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Preview</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Review the app colors before applying them.
        </p>
      </div>

      <div
        className="overflow-hidden rounded-lg border shadow-sm"
        style={{ backgroundColor: shellBackground, borderColor: neutralBorder, color: foreground }}
      >
        <div
          className="flex h-9 items-center justify-between border-b px-3"
          style={{ backgroundColor: card, borderColor: primary, color: cardForeground }}
        >
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: primary }} />
            <span className="text-[11px] font-semibold">SmartCHG</span>
          </div>
          <span className="h-5 w-5 rounded-full" style={{ backgroundColor: mixWithWhite(primary, 18) }} />
        </div>

        <div className="grid min-h-[190px] grid-cols-[3.25rem_1fr]">
          <div
            className="border-r px-2.5 py-3"
            style={{ backgroundColor: neutralSurface, borderColor: neutralBorder }}
          >
            <div
              className="flex h-8 w-8 items-center justify-center rounded-md shadow-sm"
              style={{ backgroundColor: primary, color: primaryForeground }}
            >
              <Settings className="h-3.5 w-3.5" />
            </div>
          </div>

          <div className="p-3" style={{ backgroundColor: shellBackground }}>
            <div
              className="overflow-hidden rounded-md border shadow-sm"
              style={{ backgroundColor: neutralSurface, borderColor: neutralBorder, color: foreground }}
            >
              <div className="flex border-b" style={{ borderColor: neutralBorder }}>
                <div
                  className="border-b-2 px-3 py-2 text-[11px] font-semibold"
                  style={{ backgroundColor: activeTint, borderColor: primary, color: primary }}
                >
                  Appearance
                </div>
                <div className="px-3 py-2 text-[11px] font-semibold" style={{ color: mutedForeground }}>
                  Dashboard
                </div>
              </div>

              <div className="space-y-3 p-3">
                <div>
                  <p className="text-xs font-semibold" style={{ color: foreground }}>Appearance</p>
                  <p className="mt-0.5 text-[11px]" style={{ color: mutedForeground }}>
                    Theme and chart palette
                  </p>
                </div>

                <div
                  className="rounded-md border p-3"
                  style={{ backgroundColor: neutralSurface, borderColor: neutralBorder, color: foreground }}
                >
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold">Performance view</p>
                      <p className="mt-0.5 text-[11px]" style={{ color: mutedForeground }}>
                        Revenue, margin, and volume
                      </p>
                    </div>
                    <button
                      type="button"
                      className="rounded px-2.5 py-1 text-[10px] font-semibold"
                      style={{ backgroundColor: primary, color: primaryForeground }}
                    >
                      Apply
                    </button>
                  </div>

                  <div className="flex h-14 items-end gap-1.5 rounded p-2" style={{ backgroundColor: neutralMuted }}>
                    {chartColors.slice(0, 5).map((color, index) => (
                      <span
                        key={`${color}-${index}`}
                        className="w-full rounded-t-sm"
                        style={{ height: `${18 + index * 7}px`, backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                <div
                  className="flex items-center justify-between rounded px-2.5 py-1.5 text-[11px]"
                  style={{ backgroundColor: neutralMuted, color: foreground }}
                >
                  <span className="flex items-center gap-2">
                    <BarChart3 className="h-3 w-3" />
                    Chart palette
                  </span>
                  <span className="flex gap-1">
                    {chartColors.slice(0, 4).map((color, index) => (
                      <span
                        key={`${color}-swatch-${index}`}
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Appearance tab on the Settings page. Hosts the theme preset selector,
 * previous/next theme cycling, and the chart-palette picker — the
 * surface that used to live in the now-retired sidebar SettingsPanel
 * popover. Rendered inline (no Sheet wrapper) so it sits flush in the
 * Settings tabbed shell and is the single canonical entry point for
 * appearance preferences across the app.
 */
export const AppearanceTab: React.FC = () => {
  const editorStore = useEditorStore();
  const applyThemePreset = editorStore.applyThemePreset;
  const setChartPalette = editorStore.setChartPalette;
  const setCustomChartColors = editorStore.setCustomChartColors;
  const themePresetStore = useThemePresetStore();
  const presets = themePresetStore.getAllPresets();
  const currentPreset = editorStore.themeState.preset;
  const currentMode = editorStore.themeState.currentMode;
  const currentChartPalette = editorStore.themeState.chartPaletteId;
  const currentCustomChartColors = editorStore.themeState.customChartColors;
  const [draftPreset, setDraftPreset] = useState(currentPreset || DEFAULT_PRESET);
  const [draftChartPalette, setDraftChartPalette] = useState<ChartPaletteId>(currentChartPalette);
  const [draftCustomChartColors, setDraftCustomChartColors] = useState<string[]>(
    currentCustomChartColors?.length ? currentCustomChartColors : DEFAULT_CUSTOM_CHART_COLORS
  );

  useEffect(() => {
    setDraftPreset(currentPreset || DEFAULT_PRESET);
    setDraftChartPalette(currentChartPalette);
    setDraftCustomChartColors(currentCustomChartColors?.length ? currentCustomChartColors : DEFAULT_CUSTOM_CHART_COLORS);
  }, [currentChartPalette, currentCustomChartColors, currentPreset]);

  const draftStyles = useMemo(() => {
    if (draftPreset !== DEFAULT_PRESET && presets[draftPreset]?.styles?.[currentMode]) {
      return presets[draftPreset].styles[currentMode];
    }
    return getPresetThemeStyles(draftPreset)[currentMode];
  }, [currentMode, draftPreset, presets]);

  const draftThemeChartColors = useMemo(() => getThemeChartColors(draftStyles), [draftStyles]);
  const draftChartColors = useMemo(
    () => resolveChartColors(draftChartPalette, draftCustomChartColors, draftThemeChartColors),
    [draftChartPalette, draftCustomChartColors, draftThemeChartColors]
  );

  const normalizedCurrentCustomChartColors = currentCustomChartColors?.length
    ? currentCustomChartColors
    : DEFAULT_CUSTOM_CHART_COLORS;
  const hasCustomChartColorDraftChanges = !areStringArraysEqual(
    draftCustomChartColors,
    normalizedCurrentCustomChartColors
  );
  const isThemeDraftDefault = draftPreset === RESET_THEME_PRESET;
  const isChartDraftDefault =
    draftChartPalette === DEFAULT_CHART_PALETTE &&
    areStringArraysEqual(draftCustomChartColors, DEFAULT_CUSTOM_CHART_COLORS);

  const hasDraftChanges =
    draftPreset !== (currentPreset || DEFAULT_PRESET) ||
    draftChartPalette !== currentChartPalette ||
    hasCustomChartColorDraftChanges;

  const resetDraftToApplied = () => {
    setDraftPreset(currentPreset || DEFAULT_PRESET);
    setDraftChartPalette(currentChartPalette);
    setDraftCustomChartColors(currentCustomChartColors?.length ? currentCustomChartColors : DEFAULT_CUSTOM_CHART_COLORS);
  };

  const resetThemeDraftToDefault = () => {
    setDraftPreset(RESET_THEME_PRESET);
  };

  const resetChartDraftToDefault = () => {
    setDraftChartPalette(DEFAULT_CHART_PALETTE);
    setDraftCustomChartColors(DEFAULT_CUSTOM_CHART_COLORS);
  };

  const applyDraft = () => {
    if (draftChartPalette === 'custom' || hasCustomChartColorDraftChanges) {
      setCustomChartColors(draftCustomChartColors);
    }
    applyThemePreset(draftPreset);
    setChartPalette(draftChartPalette);
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Appearance</h2>
        <p className="text-sm text-slate-500 mt-1">
          Customize the application theme and chart palette.
        </p>
      </div>

      <div className="grid items-start gap-8 lg:grid-cols-2">
        <div className="space-y-5">
          <div>
            <h3 className="text-sm font-semibold mb-3">Theme Selection</h3>
            <div className="flex max-w-md items-center gap-2">
              <ThemePresetSelect
                withCycleThemes={false}
                value={draftPreset}
                onValueChange={setDraftPreset}
                className="w-full"
              />
              <IconResetButton
                label="Reset theme"
                disabled={isThemeDraftDefault}
                onClick={resetThemeDraftToDefault}
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Chart colors</h3>
            <p className="text-sm text-muted-foreground">
              Choose the color palette for dashboard charts (bars, lines, pies).
            </p>
            <div className="flex max-w-md items-center gap-2">
              <ChartColorSelector
                value={draftChartPalette}
                customColors={draftCustomChartColors}
                themeColors={draftThemeChartColors}
                className="w-full"
                onValueChange={setDraftChartPalette}
                onCustomColorsChange={setDraftCustomChartColors}
              />
              <IconResetButton
                label="Reset chart colors"
                disabled={isChartDraftDefault}
                onClick={resetChartDraftToDefault}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <AppearancePreview styles={draftStyles} chartColors={draftChartColors} />

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" size="sm" onClick={resetDraftToApplied} disabled={!hasDraftChanges}>
              Cancel
            </Button>
            <Button size="sm" onClick={applyDraft} disabled={!hasDraftChanges}>
              Apply
            </Button>
          </div>
        </div>
      </div>
      </div>
    </TooltipProvider>
  );
};

export default AppearanceTab;
